import { useState, useCallback, useEffect } from 'react';
import type { Lead, Turma } from '@/mocks/admin';
import { isConfigured } from '@/lib/supabase';

import { leadsService } from '@/services/leads';
import { turmasService } from '@/services/turmas';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminLeads from './components/AdminLeads';
import AdminFinanceiro from './components/AdminFinanceiro';
import ProgramFacilitadores from './components/ProgramFacilitadores';
import ProgramEnrollments from './components/ProgramEnrollments';
import ProgramModulos from './components/ProgramModulos';
import ProgramAgenda from './components/ProgramAgenda';
import ProgramFinanceiro from './components/ProgramFinanceiro';

type Aba = 'dashboard' | 'leads' | 'financeiro' | 'programa';
type SubAba = 'enrollments' | 'modulos' | 'facilitadores' | 'agenda';

export default function AdminPage() {
  const [autenticado, setAutenticado] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<Aba>('dashboard');
  const [subAbaAtiva, setSubAbaAtiva] = useState<SubAba>('enrollments');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const loadData = useCallback(async () => {
    setRefreshing(true);
    const [leadsData, turmasData] = await Promise.all([
      leadsService.getAll(),
      turmasService.getAll(),
    ]);
    setLeads(leadsData);
    setTurmas(turmasData);
    setRefreshing(false);
  }, []);

  // Restore auth from sessionStorage on mount
  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth');
    if (auth === '1') setAutenticado(true);
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (autenticado) loadData();
  }, [autenticado, loadData]);

  const handleLogin = () => {
    sessionStorage.setItem('admin_auth', '1');
    setAutenticado(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setAutenticado(false);
  };

  const handleRefresh = async () => {
    await loadData();
  };

  const handleStatusChange = async (leadId: string, status: Lead['status']) => {
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status } : l));
    if (isConfigured) {
      setSaveStatus('saving');
      const ok = await leadsService.updateStatus(leadId, status);
      if (ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
        loadData();
      }
    }
  };

  const handleTurmaChange = async (leadId: string, turmaId: string | null) => {
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, turmaId } : l));
    if (isConfigured) {
      const ok = await leadsService.assignTurma(leadId, turmaId);
      if (!ok) loadData();
    }
  };

  const handleLeadUpdate = async (leadId: string, updates: Partial<Lead>) => {
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, ...updates } : l));
    if (isConfigured) {
      await leadsService.update(leadId, updates);
    }
  };

  const handleDelete = async (leadId: string) => {
    if (!isConfigured) return;
    const leadExcluido = leads.find((l) => l.id === leadId) ?? null;
    setLeads((prev) => prev.filter((l) => l.id !== leadId));
    const ok = await leadsService.delete(leadId);
    if (!ok && leadExcluido) {
      setLeads((prev) => [leadExcluido, ...prev]);
    }
  };

  if (!autenticado) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const totalLeads = leads.length;
  const novos = leads.filter((l) => l.status === 'novo').length;
  const confirmados = leads.filter((l) => l.status === 'confirmado').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Topbar */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 border border-amber-500/30 rounded-lg">
              <i className="ri-shield-keyhole-line text-amber-600 text-sm" />
            </div>
            <div>
              <span className="text-gray-900 font-black text-sm">CRM Interno</span>
              <span className="text-gray-400 text-xs ml-2 hidden sm:inline">Transformação Real 60D</span>
            </div>
          </div>

          {/* Abas */}
          <nav className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {([
              { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
              { id: 'leads', label: 'Leads', icon: 'ri-user-line' },
              { id: 'financeiro', label: 'Financeiro', icon: 'ri-money-dollar-circle-line' },
              { id: 'programa', label: 'Programa', icon: 'ri-briefcase-line' },
            ] as { id: Aba; label: string; icon: string }[]).map((aba) => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${abaAtiva === aba.id ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <i className={`${aba.icon} text-sm`} />
                <span className="hidden sm:inline">{aba.label}</span>
                {aba.id === 'leads' && novos > 0 && (
                  <span className="bg-amber-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">{novos}</span>
                )}
                {aba.id === 'financeiro' && confirmados > 0 && (
                  <span className="bg-emerald-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">{confirmados}</span>
                )}
              </button>
            ))}
          </nav>

          {/* Ações + logout */}
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-4 text-xs">
              <span className="text-gray-400">{totalLeads} leads</span>
              <span className="text-emerald-600 font-bold">{confirmados} confirmados</span>
              {isConfigured ? (
                <span className="flex items-center gap-1 text-emerald-500 font-bold">
                  <i className="ri-database-2-line" /> DB conectado
                </span>
              ) : (
                <span className="flex items-center gap-1 text-amber-500 font-bold">
                  <i className="ri-error-warning-line" /> Modo demo (sem DB)
                </span>
              )}
              {saveStatus === 'saving' && (
                <span className="flex items-center gap-1 text-blue-500 font-bold animate-pulse">
                  <i className="ri-loader-4-line animate-spin" /> Salvando...
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="flex items-center gap-1 text-emerald-500 font-bold">
                  <i className="ri-check-line" /> Salvo!
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="flex items-center gap-1 text-red-500 font-bold">
                  <i className="ri-error-warning-line" /> Erro ao salvar
                </span>
              )}
            </div>

            {/* Botão Atualizar */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Atualizar dados"
              className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-xs font-bold px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={`ri-refresh-line text-sm ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{refreshing ? 'Atualizando...' : 'Atualizar'}</span>
            </button>

            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-400 hover:text-gray-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line text-sm" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Título da aba */}
        <div className="mb-6">
          {abaAtiva === 'dashboard' && (
            <div>
              <h1 className="text-gray-900 font-black text-2xl">Dashboard</h1>
              <p className="text-gray-400 text-sm mt-1">Visão geral das turmas e leads</p>
            </div>
          )}
          {abaAtiva === 'leads' && (
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-gray-900 font-black text-2xl">Gestão de Leads</h1>
                <p className="text-gray-400 text-sm mt-1">{totalLeads} leads cadastrados · {novos} novos</p>
              </div>
            </div>
          )}
          {abaAtiva === 'financeiro' && (
            <div>
              <h1 className="text-gray-900 font-black text-2xl">Financeiro</h1>
              <p className="text-gray-400 text-sm mt-1">Confirmações de pagamento e acompanhamento de inscritos</p>
            </div>
          )}
          {abaAtiva === 'programa' && (
            <div>
              <h1 className="text-gray-900 font-black text-2xl">Gestão do Programa</h1>
              <p className="text-gray-400 text-sm mt-1">Módulos, facilitadores, agenda e entregas</p>
              <nav className="flex items-center gap-1 mt-4 bg-gray-100 rounded-xl p-1 w-fit">
                {([
                  { id: 'enrollments', label: 'Inscrições', icon: 'ri-file-list-line' },
                  { id: 'modulos', label: 'Módulos', icon: 'ri-stack-line' },
                  { id: 'facilitadores', label: 'Facilitadores', icon: 'ri-user-star-line' },
                  { id: 'agenda', label: 'Agenda', icon: 'ri-calendar-line' },
                ] as { id: SubAba; label: string; icon: string }[]).map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setSubAbaAtiva(sub.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${subAbaAtiva === sub.id ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <i className={`${sub.icon} text-sm`} />
                    <span>{sub.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>

        {abaAtiva === 'dashboard' && (
          <AdminDashboard
            leads={leads}
            turmas={turmas}
            onStatusChange={handleStatusChange}
            onTurmaChange={handleTurmaChange}
          />
        )}
        {abaAtiva === 'leads' && (
          <AdminLeads
            leads={leads}
            turmas={turmas}
            onStatusChange={handleStatusChange}
            onTurmaChange={handleTurmaChange}
            onDelete={handleDelete}
            onUpdate={handleLeadUpdate}
          />
        )}
        {abaAtiva === 'financeiro' && (
          <AdminFinanceiro
            leads={leads}
            turmas={turmas}
            onStatusChange={handleStatusChange}
            onLeadUpdate={handleLeadUpdate}
          />
        )}
        {abaAtiva === 'programa' && (
          <>
            {subAbaAtiva === 'enrollments' && <ProgramEnrollments />}
            {subAbaAtiva === 'modulos' && <ProgramModulos />}
            {subAbaAtiva === 'facilitadores' && <ProgramFacilitadores />}
            {subAbaAtiva === 'agenda' && <ProgramAgenda />}
          </>
        )}
      </main>

    </div>
  );
}